import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiCheckCircle, FiAlertCircle, FiDownload, FiHome } from 'react-icons/fi'

export default function PaymentConfirmation() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paymentStatus, setPaymentStatus] = useState(null)

  const reference = searchParams.get('reference')
  const orderId = searchParams.get('orderId')
  const sessionId = searchParams.get('session_id')
  const gateway = searchParams.get('gateway')
  const canceled = searchParams.get('canceled')

  useEffect(() => {
    if (canceled) {
      setPaymentStatus('canceled')
      setLoading(false)
      return
    }

    if (sessionId && gateway === 'stripe') {
      verifyStripePayment()
    } else if (reference && orderId) {
      verifyPayment()
    } else {
      setLoading(false)
    }
  }, [reference, orderId, sessionId, gateway, canceled])

  const verifyStripePayment = async () => {
    try {
      setLoading(true)

      console.log('[PaymentConfirmation] Verifying Stripe payment:', { sessionId, orderId })

      // Verify Stripe session with backend
      const response = await axios.post('/store-payments/verify-stripe-payment', {
        sessionId,
        orderId
      })

      if (response.data.success) {
        setPaymentStatus('success')
        
        // Fetch order details
        const orderRes = await axios.get(`/store-payments/order/${orderId}/status`)
        setOrder(orderRes.data.data)

        toast.success('Payment verified successfully!')
      } else {
        setPaymentStatus('failed')
        toast.error(response.data.message || 'Payment verification failed')
      }
    } catch (error) {
      console.error('Error verifying Stripe payment:', error)
      setPaymentStatus('failed')
      toast.error(error.response?.data?.message || 'Failed to verify payment')
    } finally {
      setLoading(false)
    }
  }

  const verifyPayment = async () => {
    try {
      setLoading(true)

      // Verify with backend
      const response = await axios.post('/store-payments/verify-payment', {
        reference,
        orderId
      })

      if (response.data.success) {
        setPaymentStatus('success')
        
        // Fetch order details
        const orderRes = await axios.get(`/store-payments/order/${orderId}/status`)
        setOrder(orderRes.data.data)

        toast.success('Payment verified successfully!')
      } else {
        setPaymentStatus('failed')
        toast.error(response.data.message || 'Payment verification failed')
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      setPaymentStatus('failed')
      toast.error('Failed to verify payment')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (paymentStatus === 'success' && order) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200 p-8 text-center">
              <FiCheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
              <p className="text-gray-600">Thank you for your order.</p>
            </div>

            {/* Order Details */}
            <div className="p-8 space-y-8">
              {/* Order ID */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Order ID</p>
                <p className="text-2xl font-bold text-gray-900 font-mono">{order.orderId}</p>
              </div>

              {/* Order Summary */}
              <div className="border-t border-b border-gray-200 py-6 space-y-4">
                <h2 className="font-semibold text-gray-900">Order Summary</h2>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">{order.currency} {order.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium text-gray-900 italic text-green-600">Tax Included</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                    <span>Total Amount Paid</span>
                    <span className="text-primary-600">{order.currency} {order.total}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Payment Information</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method</span>
                    <span className="font-medium text-gray-900">
                      {order?.paymentGateway === 'stripe' ? 'Stripe' : 'Paystack'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Status</span>
                    <span className="flex items-center gap-2">
                      <FiCheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-600">Completed</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference</span>
                    <span className="font-mono text-gray-900 text-xs">{order.reference || reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Timestamp</span>
                    <span className="text-gray-900">
                      {new Date(order.completedAt || order.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Order Placed By</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-900">{order.email}</p>
                  <p className="text-gray-600">A confirmation email has been sent to your email address.</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
                >
                  <FiHome className="w-4 h-4" /> Continue Shopping
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  <FiDownload className="w-4 h-4" /> Print Receipt
                </button>
              </div>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>If you have any questions about your order, please contact us.</p>
          </div>
        </div>
      </div>
    )
  }

  if (paymentStatus === 'failed' || (!loading && paymentStatus === null) || paymentStatus === 'canceled') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Error Header */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 border-b border-red-200 p-8 text-center">
              <FiAlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {paymentStatus === 'canceled' ? 'Payment Canceled' : 'Payment Failed'}
              </h1>
              <p className="text-gray-600">
                {paymentStatus === 'canceled' 
                  ? 'You canceled the payment process' 
                  : 'Unable to process your payment'
                }
              </p>
            </div>

            <div className="p-8 space-y-6">
              {paymentStatus !== 'canceled' && (
                <>
                  <p className="text-gray-600">
                    Your payment could not be processed. This could be due to:
                  </p>

                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li>Insufficient funds in your account</li>
                    <li>Incorrect card details</li>
                    <li>Transaction declined by your bank</li>
                    <li>Network connectivity issues</li>
                    <li>Using live keys instead of test keys</li>
                  </ul>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      For testing, use card number: 4242 4242 4242 4242 with any future expiry and CVC.
                    </p>
                  </div>
                </>
              )}

              <div className="flex gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Return to Store
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
